import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input } from '@mlm/ui';
import { Plus, Trash2 } from 'lucide-react';
import { listProducts, type Product } from '../../lib/api';

export type RealtimeUnilevelProduct = {
  product_id: string;
  /** Index 0 = Level 0 buyer cashback; index k = Level-k upline amount. */
  level_amounts: number[];
};

export type RealtimeUnilevelConfig = {
  products: RealtimeUnilevelProduct[];
};

export function emptyRealtimeUnilevelConfig(): RealtimeUnilevelConfig {
  return { products: [] };
}

export function RealtimeUnilevelEditor({
  value,
  onChange,
  readOnly,
}: {
  value: RealtimeUnilevelConfig;
  onChange: (next: RealtimeUnilevelConfig) => void;
  readOnly?: boolean;
}) {
  const productsQ = useQuery({
    queryKey: ['admin-products', 'realtime-unilevel-pick'],
    queryFn: () => listProducts({ pageSize: 200 }),
  });
  const products = productsQ.data ?? [];
  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const rows = value.products ?? [];
  const selectedIds = new Set(rows.map((r) => r.product_id));
  const [search, setSearch] = useState('');

  const available = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter(
      (p) =>
        !selectedIds.has(p.id) &&
        (term === '' || p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term)),
    );
  }, [products, search, selectedIds]);

  function update(next: RealtimeUnilevelProduct[]) {
    onChange({ ...value, products: next });
  }
  function addProduct(id: string) {
    if (selectedIds.has(id)) return;
    update([...rows, { product_id: id, level_amounts: [100] }]);
  }
  function removeProduct(idx: number) {
    update(rows.filter((_, i) => i !== idx));
  }
  function setAmount(idx: number, level: number, amount: number) {
    const next = rows.map((r) => ({ ...r, level_amounts: [...r.level_amounts] }));
    next[idx]!.level_amounts[level] = amount;
    update(next);
  }
  function addLevel(idx: number) {
    const next = rows.map((r) => ({ ...r, level_amounts: [...r.level_amounts] }));
    next[idx]!.level_amounts.push(0);
    update(next);
  }
  function removeLevel(idx: number, level: number) {
    const next = rows.map((r) => ({ ...r, level_amounts: [...r.level_amounts] }));
    next[idx]!.level_amounts = next[idx]!.level_amounts.filter((_, i) => i !== level);
    update(next);
  }

  return (
    <div className="space-y-5">
      <p className="text-[11px] text-muted-foreground">
        Fires the instant a member buys a configured product (no maintenance / qualification). Each
        product pays a FIXED cash amount per level: Level 0 is the buyer's own cashback; Levels 1+
        pay the sponsor uplines. Amounts scale by quantity. A level with no upline is unpaid.
      </p>

      {rows.length === 0 ? (
        <p className="rounded-md border p-4 text-xs text-muted-foreground">
          No trigger products yet. Add one below to start paying realtime cashback.
        </p>
      ) : (
        <div className="space-y-4">
          {rows.map((row, idx) => {
            const product = byId.get(row.product_id);
            return (
              <div key={row.product_id} className="space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {product ? product.name : row.product_id}
                    </div>
                    {product ? (
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {product.sku}
                        {!product.active && ' · inactive'}
                      </div>
                    ) : (
                      <div className="text-[10px] text-destructive">
                        Product not found (deleted?) — remove this row.
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <Button variant="outline" size="sm" onClick={() => removeProduct(idx)}>
                      Remove product
                    </Button>
                  )}
                </div>

                <table className="w-full overflow-hidden rounded-md border text-sm">
                  <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="w-40 px-3 py-2 font-medium">Level</th>
                      <th className="px-3 py-2 font-medium">Amount</th>
                      <th className="w-12 px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.level_amounts.map((amt, lvl) => (
                      <tr key={lvl} className="border-t">
                        <td className="px-3 py-2 text-xs">
                          {lvl === 0 ? (
                            <span className="font-mono">L0 — buyer cashback</span>
                          ) : (
                            <span className="font-mono">L{lvl}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            className="h-8 w-36 rounded-md border bg-background px-2 text-sm disabled:opacity-50"
                            value={amt}
                            disabled={readOnly}
                            step={0.01}
                            min={0}
                            onChange={(e) => setAmount(idx, lvl, Number(e.target.value) || 0)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          {!readOnly && row.level_amounts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLevel(idx, lvl)}
                              className="text-muted-foreground transition-colors hover:text-destructive"
                              aria-label={`Remove level ${lvl}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!readOnly && (
                  <Button size="sm" variant="outline" onClick={() => addLevel(idx)}>
                    <Plus className="mr-1 h-3 w-3" />
                    Add level
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
        <div className="space-y-2 rounded-md border p-3">
          <h3 className="text-sm font-medium">Add trigger product</h3>
          <Input
            placeholder="Search by name or SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {productsQ.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : available.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {products.length === 0 ? 'No products yet — add products first.' : 'No matches.'}
            </p>
          ) : (
            <ul className="max-h-60 divide-y overflow-y-auto">
              {available.map((p: Product) => (
                <li key={p.id} className="flex items-center gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{p.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{p.sku}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => addProduct(p.id)}>
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
